import { useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link, navigate } from '../../lib/router';
import { AsyncButton, Badge, Button, Card, cx, Empty, ErrorBox, Field, Input, Loading, Modal, PageHeader, Select, Textarea, useToast } from '../../ui/components';

interface Reason { code: string; value?: string | number }
interface AtRisk {
  personId: string; name: string; email: string | null; phone: string | null; plan: string | null;
  lastVisitDays: number | null; visits30: number; tokensLeft: number; score: number; level: 'low' | 'medium' | 'high'; reasons: Reason[];
}

/** The members most likely to leave, with the two actions that keep them: a message or a call task. */
export function RetentionPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const radar = useApi(() => api<AtRisk[]>(`${T(gym.slug)}/admin/retention`), []);
  const [level, setLevel] = useState('');
  const [message, setMessage] = useState<AtRisk | null>(null);
  const [draft, setDraft] = useState({ channel: 'email', subject: '', body: '' });

  const rows = (radar.data ?? []).filter((r) => !level || r.level === level);
  const reasonText = (r: Reason): string => i18n.t(`retention.reason_${r.code}`, { value: r.value ?? '' });

  return (
    <div className="page">
      <PageHeader title={i18n.t('retention.title')} subtitle={i18n.t('retention.subtitle')}
        actions={<Select aria-label={i18n.t('retention.filter_level')} value={level} placeholder={i18n.t('common.all')} onChange={(e) => setLevel(e.target.value)}
          options={['high', 'medium', 'low'].map((l) => ({ value: l, label: i18n.t(`retention.level_${l}`) }))} />} />

      {radar.error ? <ErrorBox error={radar.error} onRetry={radar.reload} /> : !radar.data ? <Loading /> : rows.length === 0 ? (
        <Empty icon="✓">{i18n.t('retention.none')}</Empty>
      ) : (
        <>
          <div className="stats">
            <div className="stat"><div className="stat-label">{i18n.t('retention.level_high')}</div><div className="stat-value">{(radar.data ?? []).filter((r) => r.level === 'high').length}</div></div>
            <div className="stat"><div className="stat-label">{i18n.t('retention.level_medium')}</div><div className="stat-value">{(radar.data ?? []).filter((r) => r.level === 'medium').length}</div></div>
            <div className="stat"><div className="stat-label">{i18n.t('retention.at_risk')}</div><div className="stat-value">{radar.data!.length}</div></div>
          </div>
          {rows.map((r) => (
            <Card key={r.personId} className={cx('risk', `risk-${r.level}`)}>
              <div className="row between wrap">
                <div>
                  <Link to={`${gym.base}/admin/clients/${r.personId}`}><strong>{r.name}</strong></Link>
                  <div className="small muted">
                    {[r.plan, r.lastVisitDays === null ? i18n.t('retention.never_visited') : i18n.t('retention.last_visit', { days: r.lastVisitDays }),
                      i18n.t('retention.visits_30', { count: r.visits30 }), r.tokensLeft ? i18n.t('retention.tokens_left', { count: r.tokensLeft }) : null]
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="row gap-sm">
                  <Badge value={r.level} label={`${i18n.t(`retention.level_${r.level}`)} · ${r.score}`} />
                  {r.phone && <a className="btn btn-ghost btn-sm" href={`tel:${r.phone}`}>{i18n.t('crm.call')}</a>}
                  <Button size="sm" variant="secondary" onClick={() => { setMessage(r); setDraft({ channel: 'email', subject: i18n.t('retention.default_subject', { gym: gym.gym.name }), body: i18n.t('retention.default_body') }); }}>
                    {i18n.t('retention.message')}
                  </Button>
                  <AsyncButton size="sm" variant="ghost" onClick={async () => {
                    await api(`${T(gym.slug)}/admin/r/tasks`, { body: { title: i18n.t('retention.task_title', { name: r.name }), person_id: r.personId, due_at: new Date(Date.now() + 86_400_000).toISOString(), status: 'open' } });
                    toast.ok(i18n.t('retention.task_created'));
                  }}>{i18n.t('retention.task')}</AsyncButton>
                </div>
              </div>
              <div className="row gap-sm wrap">
                {r.reasons.map((x) => <span key={x.code} className="badge badge-neutral">{reasonText(x)}</span>)}
              </div>
            </Card>
          ))}
        </>
      )}

      {message && (
        <Modal title={i18n.t('retention.message_to', { name: message.name })} onClose={() => setMessage(null)} footer={
          <AsyncButton disabled={!draft.body.trim()} onClick={async () => {
            await api(`${T(gym.slug)}/admin/persons/${message.personId}/message`, { body: draft });
            toast.ok(i18n.t('retention.message_sent'));
            setMessage(null);
          }}>{i18n.t('messages.send')}</AsyncButton>
        }>
          <div className="grid-2">
            <Field label={i18n.field('channel')}>
              <Select value={draft.channel} onChange={(e) => setDraft({ ...draft, channel: e.target.value })}
                options={['email', 'push', 'sms'].map((c) => ({ value: c, label: i18n.option(c) }))} />
            </Field>
            {draft.channel === 'email' && <Field label={i18n.field('subject')}><Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} /></Field>}
          </div>
          <Field label={i18n.field('body')} hint={i18n.t('messages.placeholders')}>
            <Textarea rows={6} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          </Field>
          <Button variant="ghost" onClick={() => navigate(`${gym.base}/admin/clients/${message.personId}`)}>{i18n.t('inbox.open_client')}</Button>
        </Modal>
      )}
    </div>
  );
}
